/**
 * Counsel vendor UI.
 * Card footnote, hire acknowledgment, and listing attestation.
 * Hire clickwrap stays on DOCUMENT_VERSIONS (2026-10-09).
 * Attestation clickwrap uses ATTESTATION_DOCUMENT_VERSIONS (2026-10-16)
 * so new acceptances record the updated text and older rows keep theirs.
 * This is not a policy page. Web and the iOS build both read these strings.
 */

const { VENDOR_APPLICATION_BOX_3 } = require('./vendorApplication');
const { isFloridaHome } = require('./homeRegion');

const DOCUMENT_VERSIONS = '2026-10-09;terms#vendor-disclaimer;terms#vendor-submissions;dpa#2A';
const ATTESTATION_DOCUMENT_VERSIONS = '2026-10-16;terms#vendor-disclaimer;terms#vendor-submissions;dpa#2A';
const LICENSE_LOOKUP_URL = 'https://www.myfloridalicense.com';
const VENDOR_DISCLAIMER_PATH = '/terms#vendor-disclaimer';
const VENDOR_SUBMISSIONS_PATH = '/terms#vendor-submissions';
const DPA_PATH = '/dpa';

const VENDOR_VETTING_NOTICE =
  "Pacarion doesn't vet or guarantee vendors. Verify licenses and insurance before hiring.";
const FLORIDA_LICENSE_LINK = 'Check a Florida license →';
const GENERIC_LICENSE_LINK = 'Check the contractor license with your state';
const VENDOR_DISCLAIMER_LINK = 'Vendor disclaimer';

const BEFORE_YOU_HIRE_NOTICE =
  "Before you hire: Pacarion LLC doesn't represent, endorse, vet, or guarantee any vendor's work, licensing, insurance, pricing, quality, or conduct. Any work is between you and the vendor. Check licenses at myfloridalicense.com, and confirm insurance and written estimates. See the Terms, Section 10.3 (/terms#vendor-disclaimer).";

const GENERIC_BEFORE_YOU_HIRE_NOTICE =
  "Before you hire: Pacarion LLC doesn't represent, endorse, vet, or guarantee any vendor's work, licensing, insurance, pricing, quality, or conduct. Any work is between you and the vendor. Check the contractor license with your state, and confirm insurance and written estimates. See the Terms, Section 10.3 (/terms#vendor-disclaimer).";

const VENDOR_ATTESTATION_TEXT =
  "I state that this vendor information is accurate, that I'm responsible for this listing, and that I won't describe the vendor as vetted, insured, licensed, or checked by Pacarion. If I have any referral, compensation, or other business arrangement with this vendor, it is my own arrangement, not Pacarion's. I'll disclose it directly to my clients as the law requires, and it complies with RESPA (12 U.S.C. §2607), Chapter 475, Florida Statutes, and my brokerage's policies, including receiving any compensation only through my employing broker where required. I won't accept anything of value from a lender, title or settlement agent, insurer, home inspector, appraiser, or home-warranty company for this listing or for referrals. I'll defend, indemnify, and hold harmless Pacarion LLC from claims arising from this listing or any arrangement I have with this vendor, except to the extent caused by Pacarion's gross negligence, fraud, or intentional misconduct.";

const PACARION_NO_VENDOR_PAYMENT =
  'Pacarion does not receive referral fees, commissions, or other compensation from Vendors, and does not charge for, rank, or sell Vendor placement. An Agent, organization, or other user may have their own referral or compensation arrangement with a Vendor. Any such arrangement is solely between them and the Vendor, and they alone are responsible for disclosing it and complying with the law that applies to it. Pacarion is not a party to, does not facilitate, and is not responsible for any such arrangement.';

const AGENT_VENDOR_COMPENSATION_OBLIGATION =
  "to disclose any referral or compensation arrangement with a Vendor to your clients as the law requires, to comply with RESPA, Chapter 475, Florida Statutes, and your brokerage's rules, and never to accept anything of value from a lender, title or settlement agent, insurer, home inspector, appraiser, or home-warranty company for referrals";

const VENDOR_LIST_NO_PAYMENT = "Pacarion doesn't receive payment from these vendors.";

const PROVIDER_LIST_NOTICE_TAIL =
  "Pacarion doesn't receive payment from these providers, and none are paid placements. Your sponsor may or may not receive fees from some providers. Ask them for details. Pacarion doesn't vet or guarantee providers. Check licenses and insurance before hiring.";

const AGENT_PROVIDER_LINE =
  "From your sponsor's provider list. Pacarion doesn't vet or guarantee providers. Check licenses and insurance before hiring.";

const SAVED_PROVIDER_LINE =
  "From your saved providers. Pacarion doesn't vet or guarantee providers.";

const LEGACY_SAVED_PROVIDER_LINE =
  "From your saved providers. Pacarion doesn't vet or guarantee providers. Check licenses and insurance before hiring.";

const PROVIDER_DETAIL_FOOTER = "Pacarion doesn't vet or guarantee providers.";

const AI_VENDOR_BOUNDARY =
  "Only mention providers from the list in this prompt. That list is this homeowner's agent or team list, or the homeowner's saved providers. Do not invent a provider, do not search for one, and do not name a company that is not on the list. Never call a provider recommended, best, licensed, or trusted.";

const BEFORE_YOU_HIRE = 'before_you_hire';
const VENDOR_ATTESTATION = 'vendor_attestation';
const CONTACT_KINDS = ['phone', 'email', 'website'];
const ROLES = ['agent', 'homeowner', 'vendor'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value) {
  return UUID_RE.test(String(value || ''));
}

function displayName(agentName) {
  return String(agentName || '').replace(/\s+/g, ' ').trim() || 'your sponsor';
}

function addedByLine(agentName) {
  return `Added by ${displayName(agentName)}. Not a paid placement by Pacarion.`;
}

function creatorNameForVendor(vendor, namesById) {
  if (!vendor || !vendor.created_by || !namesById) return '';
  return String(namesById[vendor.created_by] || '').trim();
}

function footnoteName(vendors, namesById, viewerName) {
  const names = (vendors || []).map((vendor) => {
    const creator = creatorNameForVendor(vendor, namesById);
    if (creator) return creator;
    if (vendor && vendor.visibility === 'team') return '';
    return viewerName || '';
  });
  if (!names.length) return viewerName || '';
  const unique = [...new Set(names.map((name) => String(name || '').trim()).filter(Boolean))];
  if (unique.length === 1) return unique[0];
  return viewerName || '';
}

function sameTeam(profile, vendor) {
  if (!profile.team_id || !vendor.team_id) return true;
  return profile.team_id === vendor.team_id;
}

function isTeamOwner(profile) {
  return profile.is_team_owner === true || profile.role === 'owner';
}

function isVendorCreator(profile, vendor) {
  if (!profile.id) return false;
  return vendor.created_by === profile.id || vendor.agent_id === profile.id;
}

function canManageVendor(profile, vendor) {
  if (!profile || !vendor) return false;
  if (vendor.visibility === 'team') {
    if (!sameTeam(profile, vendor)) return false;
    return isTeamOwner(profile) || isVendorCreator(profile, vendor);
  }
  return isVendorCreator(profile, vendor);
}

function canDeleteVendor(profile, vendor) {
  if (!profile || !vendor) return false;
  if (vendor.visibility === 'team') {
    if (!sameTeam(profile, vendor)) return false;
    return isTeamOwner(profile);
  }
  return isVendorCreator(profile, vendor);
}

function providerListSubject({ agentName, plan, teamId, orgName, subject } = {}) {
  const given = String(subject || '').replace(/\s+/g, ' ').trim();
  if (given) return given;
  const name = displayName(agentName);
  if (plan === 'enterprise') {
    const org = String(orgName || '').replace(/\s+/g, ' ').trim();
    return org || name;
  }
  if (teamId || plan === 'team') return `${name} and their team`;
  return name;
}

function providerListNotice(input) {
  const subject = typeof input === 'string'
    ? providerListSubject({ subject: input })
    : providerListSubject(input);
  return `Recommended by ${subject}. ${PROVIDER_LIST_NOTICE_TAIL}`;
}

function vendorListHeaderParts(agentName) {
  const name = displayName(agentName);
  return {
    name,
    before: `Recommended by ${name}. `,
    emphasis: '',
    after: PROVIDER_LIST_NOTICE_TAIL,
  };
}

function vendorListHeader(agentName) {
  return providerListNotice({ agentName });
}

function providerSuggestionLine(source) {
  return source === 'saved' ? SAVED_PROVIDER_LINE : AGENT_PROVIDER_LINE;
}

function vendorListSource(vendor) {
  if (!vendor) return null;
  if (vendor.client_id || vendor.listSource === 'saved' || vendor.list_source === 'saved') return 'saved';
  if (vendor.listSource === 'agent' || vendor.list_source === 'agent') return 'agent';
  if (vendor.agent_id || vendor.team_id || vendor.visibility) return 'agent';
  return null;
}

function formatSuggestedVendors(vendors) {
  return (vendors || []).map((vendor) => {
    const source = vendorListSource(vendor);
    if (!vendor || !vendor.name || !source) return null;
    const names = [];
    if (Array.isArray(vendor.category)) names.push(...vendor.category);
    else if (vendor.category) names.push(vendor.category);
    if (Array.isArray(vendor.categories)) names.push(...vendor.categories);
    const categories = names.filter(Boolean).join(', ');
    const line = providerSuggestionLine(source);
    return {
      vendor,
      source,
      line,
      text: `- ${vendor.name}${categories ? ` (${categories})` : ''}: ${vendor.phone || ''}${vendor.discount ? ` - ${vendor.discount}` : ''}`,
    };
  }).filter(Boolean);
}

function suggestionLinePattern(line) {
  return line
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/'/g, "['\u2019]");
}

/**
 * The provider card prints the source line. Drop it from a model reply
 * so the sentence is not shown twice. A reminder to verify licenses stays.
 */
function stripProviderSuggestionLines(text) {
  let value = String(text == null ? '' : text);
  [
    AGENT_PROVIDER_LINE,
    LEGACY_SAVED_PROVIDER_LINE,
    SAVED_PROVIDER_LINE,
    "From your sponsor's provider list.",
    "From your agent's provider list.",
    'From your saved providers.',
    "Pacarion doesn't vet or guarantee providers. Check licenses and insurance before hiring.",
  ].forEach((line) => {
    value = value.replace(new RegExp(suggestionLinePattern(line), 'g'), ' ');
  });
  return value
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function documentVersionsFor(consentType) {
  if (consentType === VENDOR_ATTESTATION) return ATTESTATION_DOCUMENT_VERSIONS;
  return DOCUMENT_VERSIONS;
}

function resolveConsentActor({ authUserId, client }) {
  if (authUserId) return authUserId;
  if (!client) return null;
  return client.user_id || client.id || null;
}

function licensePrompt(home) {
  if (isFloridaHome(home)) {
    return { label: FLORIDA_LICENSE_LINK, href: LICENSE_LOOKUP_URL };
  }
  return { label: GENERIC_LICENSE_LINK, href: null };
}

function beforeYouHireNotice(home) {
  return isFloridaHome(home) ? BEFORE_YOU_HIRE_NOTICE : GENERIC_BEFORE_YOU_HIRE_NOTICE;
}

function noticeTextFor(consentType, home) {
  if (consentType === BEFORE_YOU_HIRE) return beforeYouHireNotice(home);
  if (consentType === VENDOR_ATTESTATION) return VENDOR_ATTESTATION_TEXT;
  return '';
}

function buildConsentRecord({
  userId,
  role,
  consentType,
  vendorId = null,
  ip = null,
  userAgent = null,
  now = new Date(),
  home = null,
}) {
  const noticeText = noticeTextFor(consentType, home);
  if (!noticeText) return { error: 'invalid_type' };
  if (!ROLES.includes(role)) return { error: 'invalid_role' };
  if (!userId || !isUuid(userId)) return { error: 'missing_actor' };
  const vendor = vendorId || null;
  if (vendor && !isUuid(vendor)) return { error: 'invalid_vendor' };
  const acceptedAt = now instanceof Date ? now.toISOString() : new Date(now).toISOString();
  return {
    row: {
      user_id: userId,
      role,
      consent_type: consentType,
      document_versions: documentVersionsFor(consentType),
      notice_text: noticeText,
      accepted_at: acceptedAt,
      ip: ip ? String(ip).slice(0, 80) : null,
      user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
      vendor_id: vendor,
    },
  };
}

function buildVendorSubmissionConsent({
  businessName,
  email,
  applicationId,
  ip,
  userAgent,
  now = new Date(),
}) {
  const vendorName = String(businessName || '').trim().slice(0, 200);
  const vendorEmail = String(email || '').trim().toLowerCase().slice(0, 200);
  if (!vendorName) return { error: 'missing_vendor' };
  if (applicationId && !isUuid(applicationId)) return { error: 'invalid_application' };
  const acceptedAt = now instanceof Date ? now.toISOString() : new Date(now).toISOString();
  return {
    row: {
      user_id: null,
      role: 'vendor',
      consent_type: VENDOR_ATTESTATION,
      document_versions: ATTESTATION_DOCUMENT_VERSIONS,
      notice_text: VENDOR_APPLICATION_BOX_3,
      accepted_at: acceptedAt,
      ip: ip ? String(ip).slice(0, 80) : null,
      user_agent: userAgent ? String(userAgent).slice(0, 500) : null,
      vendor_id: null,
      vendor_name: vendorName,
      vendor_email: vendorEmail || null,
      vendor_application_id: applicationId || null,
    },
  };
}

function acknowledgmentSatisfied(rows) {
  return (rows || []).some((row) => (
    row
    && row.consent_type === BEFORE_YOU_HIRE
    && row.document_versions === DOCUMENT_VERSIONS
  ));
}

function attestationAccepted(checked) {
  return checked === true;
}

function contactAttempt({ acknowledged, kind }) {
  if (!CONTACT_KINDS.includes(kind)) return { allow: false, showModal: false };
  if (acknowledged === true) return { allow: true, showModal: false };
  return { allow: false, showModal: true };
}

module.exports = {
  DOCUMENT_VERSIONS,
  ATTESTATION_DOCUMENT_VERSIONS,
  LICENSE_LOOKUP_URL,
  VENDOR_DISCLAIMER_PATH,
  VENDOR_SUBMISSIONS_PATH,
  DPA_PATH,
  VENDOR_VETTING_NOTICE,
  FLORIDA_LICENSE_LINK,
  GENERIC_LICENSE_LINK,
  VENDOR_DISCLAIMER_LINK,
  BEFORE_YOU_HIRE_NOTICE,
  GENERIC_BEFORE_YOU_HIRE_NOTICE,
  licensePrompt,
  beforeYouHireNotice,
  VENDOR_ATTESTATION_TEXT,
  PACARION_NO_VENDOR_PAYMENT,
  AGENT_VENDOR_COMPENSATION_OBLIGATION,
  VENDOR_LIST_NO_PAYMENT,
  PROVIDER_LIST_NOTICE_TAIL,
  AGENT_PROVIDER_LINE,
  SAVED_PROVIDER_LINE,
  PROVIDER_DETAIL_FOOTER,
  AI_VENDOR_BOUNDARY,
  BEFORE_YOU_HIRE,
  VENDOR_ATTESTATION,
  CONTACT_KINDS,
  isUuid,
  addedByLine,
  creatorNameForVendor,
  footnoteName,
  canManageVendor,
  canDeleteVendor,
  vendorListHeaderParts,
  vendorListHeader,
  providerListSubject,
  providerListNotice,
  providerSuggestionLine,
  vendorListSource,
  formatSuggestedVendors,
  stripProviderSuggestionLines,
  documentVersionsFor,
  resolveConsentActor,
  noticeTextFor,
  buildConsentRecord,
  buildVendorSubmissionConsent,
  acknowledgmentSatisfied,
  attestationAccepted,
  contactAttempt,
};
